import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  BiometricAuth,
  type CheckBiometryResult,
  BiometryErrorType,
  AndroidBiometryStrength,
  type AuthenticateOptions,
  type BiometryError,
  BiometryType,
} from '@aparajita/capacitor-biometric-auth';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { NotificationsService } from './notifications.service';
import { NotificationType } from '../enums/notificationtype.enum';
import { type Nav } from '../enums/nav.enum';
import { catchError, concatMap, EMPTY, from, tap, type Observable } from 'rxjs';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { UtilService } from './util.service';
import { StorageEnum } from '../enums/storage.enum';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { StorageService } from './storage.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { EncryptionService } from './encryption.service';

@Injectable({
  providedIn: 'root',
})
export class BiometricsService {
  isBrowser = false;
  bioErrors: any[] = [];
  biotypeText: any = {};
  bioInfo = {
    DeviceId: '',
    Platform: 'web',
    BioAvailable: false,
    UsernameMatch: false,
    BioTypes: [] as BiometryType[],
  };

  constructor(
    @Inject(PLATFORM_ID) platformId: string,
    private readonly encryptionS: EncryptionService,
    private readonly notiS: NotificationsService,
    private readonly storageS: StorageService,
    private readonly utilS: UtilService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  setBioTypeText(bioTypeText: any[]): void {
    this.biotypeText = bioTypeText;
  }

  setBioErrors(errors: any[]): void {
    this.bioErrors = errors;
  }

  checkBioObs(testWeb = false): Observable<CheckBiometryResult> {
    if (testWeb) {
      const bioType$ = from(
        BiometricAuth.setBiometryType('fingerprintAuthentication').then(
          async () => await BiometricAuth.checkBiometry(),
        ),
      );
      return bioType$;
    } else {
      return from(BiometricAuth.checkBiometry());
    }
  }

  handleError(result: CheckBiometryResult | null, pageName: Nav, bioError: BiometryError | null = null): any {
    let error;
    let res;
    if (result && result.code) {
      res = { ...result };
    } else if (bioError) {
      res = { code: bioError.code, reason: bioError.message };
    } else {
      res = { code: BiometryErrorType.invalidContext, reason: 'Unknown error' };
    }
    switch (res.code) {
      case BiometryErrorType.biometryNotAvailable:
        this.utilS.consoleLog('Biometry not available: ' + res.code + ' - ' + res.reason);
        break;
      case BiometryErrorType.biometryNotEnrolled:
        // show error msg that bio not enrolled
        this.utilS.consoleLog('Biometry not enrolled: ' + res.code + ' - ' + res.reason);
        error = this.bioErrors.find((err) => err.errorCode === res.code);
        this.notiS.addNotification(error.displayMessage, NotificationType.Prenotification, pageName);

        break;
      case BiometryErrorType.biometryLockout:
        // show error msg that too many attempts, locked out
        this.utilS.consoleLog('Biometry locked out: ' + res.code + ' - ' + res.reason);
        error = this.bioErrors.find((err) => err.errorCode === res.code);
        this.notiS.addNotification(error.displayMessage, NotificationType.Alert, pageName);
        this.storageS.setLocal(StorageEnum.BioLockout, true);

        break;
      case BiometryErrorType.userCancel:
        this.utilS.consoleLog('Biometry authentication cancelled by user: ' + res.code + ' - ' + res.reason);

        break;
      case BiometryErrorType.passcodeNotSet:
      case BiometryErrorType.authenticationFailed:
      case BiometryErrorType.userFallback:
      case BiometryErrorType.noDeviceCredential:
      case BiometryErrorType.notInteractive:
      case BiometryErrorType.invalidContext:
      case BiometryErrorType.systemCancel:
      case BiometryErrorType.appCancel:
        this.utilS.consoleLog('Biometry error: ' + res.code + ' - ' + res.reason);
        error = this.bioErrors.find((err) => err.errorCode === 'genericError');
        this.notiS.addNotification(error.displayMessage, NotificationType.Alert, pageName);

        break;
      case BiometryErrorType.none:
      default:
        this.utilS.consoleLog('Biometry no error: ' + JSON.stringify(res));

        break;
    }
    return res;
  }

  showBiometryPrompt(pageName: Nav, popupText: any): Observable<any> {
    return this.checkBioObs().pipe(
      concatMap((result: CheckBiometryResult) => {
        // check if biometry types are avail and update text if only one avail. else use generic biometrics text
        const authOptions = this.mapBuilderTextToAuthConfig(popupText);

        return from(BiometricAuth.authenticate(authOptions));
      }),
    );
  }

  enrollUserInBiometrics(pageName: Nav, popupText: any): Observable<any> {
    return this.showBiometryPrompt(pageName, popupText).pipe(
      concatMap(() => {
        const un = this.storageS.getSession(StorageEnum.UserName);
        const pw = this.storageS.getSession(StorageEnum.UserPW);

        return this.encryptionS.encrypt(un, pw).pipe(
          catchError((error) => {
            // show error and cancel operation??
            this.utilS.consoleLog('Encryption failed:' + JSON.stringify(error));
            const errorNotification = this.bioErrors.find((err) => err.errorCode === 'encryptionError');
            if (errorNotification) {
              this.notiS.addNotification(errorNotification.displayMessage, NotificationType.Alert, pageName);
            }
            return EMPTY;
          }),
        );
      }),
      tap((encryptResponse: any) => {
        // store encrypted creds in local storage and set up biometrics
        this.storageS.setLocal(StorageEnum.EncryptedCreds, encryptResponse.EncryptedText);
        return encryptResponse;
      }),
    );
  }

  mapBuilderTextToAuthConfig(popupText: any): AuthenticateOptions {
    const bioText = this.getAvailableBiometricsText();
    const authOptions: AuthenticateOptions = {
      reason: popupText.body.replace('{BIOTYPE}', bioText),
      androidTitle: popupText.title.replace('{BIOTYPE}', bioText),
      cancelTitle: popupText.cancelBtn,
      androidConfirmationRequired: true,
      androidBiometryStrength: AndroidBiometryStrength.weak,
      allowDeviceCredential: false,
    };
    return authOptions;
  }

  getAvailableBiometricsText(): string {
    let bioText = this.biotypeText.biometricsText;
    if (this.bioInfo.BioTypes.length === 1) {
      const bioType: BiometryType = this.bioInfo.BioTypes[0];
      switch (bioType) {
        case BiometryType.touchId:
          bioText = this.biotypeText.touchIdText;
          break;
        case BiometryType.faceId:
          bioText = this.biotypeText.faceIdText;
          break;
        case BiometryType.fingerprintAuthentication:
          bioText = this.biotypeText.fingerText;
          break;
        case BiometryType.irisAuthentication:
          bioText = this.biotypeText.irisText;
          break;
        case BiometryType.faceAuthentication:
          bioText = this.biotypeText.faceText;
          break;
        case BiometryType.none:
        default:
          bioText = this.biotypeText.biometricsText;
          break;
      }
    }
    return bioText;
  }

  getBioIcon(availableBioTypes: BiometryType[]): string {
    if (availableBioTypes.length === 1) {
      const bioType: BiometryType = availableBioTypes[0];
      switch (bioType) {
        case BiometryType.faceId:
        case BiometryType.faceAuthentication:
          return 'fa-solid fa-face-viewfinder';
        case BiometryType.fingerprintAuthentication:
        case BiometryType.touchId:
        case BiometryType.irisAuthentication:
        case BiometryType.none:
        default:
          return 'fa-solid fa-fingerprint';
      }
    }
    return 'fa-solid fa-fingerprint';
  }
}
